<script lang="ts" setup>
import countTo from 'vue-count-to'
import { onUnmounted, ref, watch } from 'vue'
import { useBrandListExposure } from '@/composables/tracks/useBrandExposure'
import { productParams } from '@/composables/tracks/paymentParameters'
import type { MemberTopupSubscribeProduct, SubscribePageConfig } from '@/composables/payment'
import { createShowPaymentMethodSelector, useSubscription } from '@/composables/payment'
import AutoRenewalNotice from '@/composables/payment/components/AutoRenewalNotice.vue'
import PaymentMethodSelector from '@/composables/payment/components/PaymentMethodSelector.vue'
import { useAxonViewItem } from '@/composables/axon/useAxonViewItem'
import { useFbqAddToCart } from '@/composables/fbq/useFbqAddToCart'
import { getDailyPrice, getSavePercent, getUnitPriceValue, shouldShowDailyPrice, shouldShowPreferentialTotal } from '@/composables/payment/utils/subscribeDailyPrice'
import { VIP_TYPE } from '@/constants'
import { brand, colors } from '@/config/brands'

const props = defineProps<{
  config: SubscribePageConfig
}>()

// 使用订阅 composable（支持多支付方式）
const {
  subscribeProducts,
  onSubscribeProducts,
  getIsImageBackground,
  isZero,
  currentPaymentMethod,
  availablePaymentMethods,
  selectPaymentMethod,
} = useSubscription('store')

// 是否显示支付方式选择器
const showPaymentMethodSelector = createShowPaymentMethodSelector(availablePaymentMethods)

// Axon view_item 上报（商品加载 + 支付方式切换均触发，Set 内部去重）
const { reportViewItem } = useAxonViewItem()
watch(subscribeProducts, (val) => {
  if (val.length)
    reportViewItem(val, currentPaymentMethod.value, 'Membership', p => p.preferentialPrice ?? p.price)
})

const { reportAddToCart } = useFbqAddToCart()
watch(subscribeProducts, (val) => {
  for (const p of val)
    reportAddToCart(p.productId, 'Membership', p.preferentialPrice ?? p.price ?? 0)
})

const active = ref(0)
const discountModel = ref(false)

const timer = ref<ReturnType<typeof setTimeout> | null>(null)
const currentDiscount: any = ref(null)
const discountNumber = ref(0)
const getDiscountPercentage = function () {
  if (subscribeProducts.value && subscribeProducts.value.length > 0) {
    let maxDiscount = 0
    let current = null
    subscribeProducts.value.forEach((item: MemberTopupSubscribeProduct) => {
      let discount = 0
      let shouldCalculate = false

      // 畅读会员：只有在 preferentialPrice 存在时才计算折扣(包括为0的情况)
      if (item.subscribeType === VIP_TYPE.ALL && (item.preferentialPrice !== undefined && item.preferentialPrice !== null)) {
        discount = (1 - (Number(item.preferentialPrice) / Number(item.price))) * 100
        shouldCalculate = true
      }

      // 折扣会员：有 discountOriginalPrice 就计算折扣
      if (item.subscribeType === VIP_TYPE.DISCOUNT && item.discountOriginalPrice) {
        discount = (1 - (Number(item.price) / Number(item.discountOriginalPrice))) * 100
        shouldCalculate = true
      }

      // 只有符合条件的会员才参与比较
      if (shouldCalculate && discount >= maxDiscount) {
        current = item
        maxDiscount = Math.round(discount)
      }
    })

    // 只要找到符合条件的会员就返回，不管折扣是多少(包括0)
    if (current)
      return [current, maxDiscount]
  }

  return false
}

function initDiscountModel() {
  if (brand === 'novelsprig')
    return

  const showDiscount = window.localStorage.getItem('showDiscount')
  if (showDiscount)
    return

  timer.value = setTimeout(() => {
    const discountResult = getDiscountPercentage()
    discountNumber.value = discountResult ? (discountResult[1] ?? 0) : 0
    currentDiscount.value = discountResult ? discountResult[0] : null

    if (!Array.isArray(discountResult)) {
      clearTimeout(timer.value!)
      return
    }

    discountModel.value = true
    window.localStorage.setItem('showDiscount', '1')
  }, 6000)
}
initDiscountModel()

function buyNow() {
  onSubscribeProducts(currentDiscount.value)
}

onUnmounted(() => {
  if (timer.value)
    clearTimeout(timer.value)
})

// 定义样式变量供 CSS v-bind 使用
const redGradient = props.config.colors.redGradient
const whiteColor = '#FFFFFF'
const purpleTextGradient = props.config.colors.purpleTextGradient
const vipBgUrl = `url(${props.config.resources.vipBg})`
const primaryColor = colors?.primary

const subscribeText1 = props.config.colors.subscribeText1
const subscribeText2 = props.config.colors.subscribeText2
const subscribeText3 = props.config.colors.subscribeText3
const subscribeText4 = props.config.colors.subscribeText4
// 图片背景卡上的金额颜色：深色品牌的 subscribeText2 与金色图卡背景接近导致金额不可见，未配置时回退 subscribeText2 保持原行为
const subscribeImagePrice = props.config.colors.subscribeImagePrice || subscribeText2
const exposureRef = useBrandListExposure<Record<string, any>>(() => subscribeProducts.value, item => productParams(item, true))
</script>

<template>
  <div ref="exposureRef" class="subscribe-cover">
    <BasicNavbar class="color-n-text-2" />

    <div class="px-20px">
      <div class="subscribe-text-2 text-20px font-bold h-44px flex items-center justify-center">
        {{ config.texts.membership }}
      </div>
      <div class="text-28px font-bold subscribe-text-1 mt-2">
        {{ config.texts.vipMembership }}
      </div>
      <div class="subscribe-text-3 text-14px">
        {{ config.texts.membershipDesc }}
      </div>
      <div class="subscribe-text-1 mt-2px text-14px">
        {{ config.texts.notJoinedYet }}
      </div>
    </div>

    <div class="member-wrap mt-2 mx-10px bg-n-bg-card b-rd-2 box-shadow px-10px pt-16px">
      <div v-for="(item, index) in subscribeProducts" :key="index" :data-exposure-index="index">
        <div
          v-if="item.isShow"
          class="px-14px pt-20px pb-16px border-rd-12px subscribe-text-2 relative mb-10px"
          :style="{
            border: active === index ? `1px solid ${primaryColor}` : `1px solid ${colors?.border.primary}`,
            background: getIsImageBackground(item)
              ? `url(${config.resources.memberSubscribeBg}) no-repeat`
              : colors.border.primary,
            backgroundSize: '100% 100%',
            color: getIsImageBackground(item) ? subscribeText4 : subscribeText2,
          }"
          @click="() => { active = index; onSubscribeProducts(item); }"
        >
          <!-- 角标, 两种会员都有 -->
          <div v-if="item.cornerTip === 1 && item.marketingCopy" class="popular-tag subscribe-tier-badge">
            {{ item.marketingCopy }}
          </div>

          <!-- 折扣会员 -->
          <div v-if="item.subscribeType === VIP_TYPE.DISCOUNT" class="flex items-center">
            <div class="absolute top-0 right-0 font-bold text-28px leading-[28px] cycleBg">
              {{ item.cycleStr }}
            </div>

            <img v-if="config.resources.showVipIcon" class="flex-shrink-0 mr-16px" :src="config.resources.vipDiscountIcon" width="22">
            <div class="flex-1">
              <div class="text-18px font-medium">
                {{ item.discountCoins }} Coins
              </div>
              <ul
                class="text-11px list-disc pl-3"
                :class="getIsImageBackground(item) ? 'subscribe-text-4' : 'subscribe-text-1'"
              >
                <li>{{ config.texts.payTemplateGetCoins?.(item.discountOnceCoins) }}</li>
                <li>{{ config.texts.payTemplateEarnCoins?.(item.discountBonusCoins, item.discountDailyCoins) }}</li>
              </ul>
            </div>
            <div class="flex-shrink-0 text-right">
              <div class="text-18px font-bold" :class="getIsImageBackground(item) ? 'subscribe-image-price' : 'subscribe-text-2'">
                <div class="price-counter min-w-60px">
                  <span aria-hidden="true" class="invisible whitespace-nowrap">$ {{ Number(item.discountOriginalPrice).toFixed(2) }}</span>
                  <span class="price-counter__value">$ <countTo
                    :start-val="Number(item.discountOriginalPrice)"
                    :end-val="Number(item.preferentialPrice || item.price)"
                    :duration="3000"
                    :decimals="2"
                  /></span>
                </div>
              </div>
              <div class="text-12px line-through" :class="getIsImageBackground(item) ? 'subscribe-text-4' : 'subscribe-text-3'">
                $ {{ item.discountOriginalPrice }}
              </div>
            </div>
          </div>

          <!-- 畅读会员 -->
          <div v-else class="flex items-center">
            <img v-if="config.resources.showVipIcon" class="flex-shrink-0 mr-16px" :src="config.resources.vipIcon" width="22">
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-6px">
                <BasicFitText class="text-18px font-medium">
                  {{ item.cycleStr }}
                </BasicFitText>
                <div
                  v-if="shouldShowDailyPrice(item, isZero)"
                  class="subscribe-save-label subscribe-page-save-label flex-shrink-0 bg-[#F74984]/15 text-[#F74984] text-10px font-medium rounded-4px px-6px py-3px lh-8px"
                >
                  SAVE {{ getSavePercent(item) }}%
                </div>
              </div>
              <!-- 覆写文案(ZKSN-251):一行放得下则单行展示,放不下按 \n 拆行并逐行缩放;接口文案保持单行截断兜底 -->
              <BasicFitLines
                v-if="item.subscribeTextOverridden"
                :text="item.subscribeText"
                class="text-13px"
                :class="getIsImageBackground(item) ? 'subscribe-text-4' : 'subscribe-text-1'"
              />
              <div
                v-else
                class="text-13px truncate"
                :class="getIsImageBackground(item) ? 'subscribe-text-4' : 'subscribe-text-1'"
              >
                {{ item.subscribeText }}
              </div>
            </div>

            <div class="flex-shrink-0 text-right">
              <!-- 免费 -->
              <div v-if="isZero(item.preferentialPrice)">
                <div class="text-18px font-bold" :class="getIsImageBackground(item) ? 'subscribe-image-price' : 'subscribe-text-2'">
                  {{ config.texts.free }}
                </div>
                <div class="text-12px line-through" :class="getIsImageBackground(item) ? 'subscribe-text-4' : 'subscribe-text-3'">
                  $ {{ item.price }}
                </div>
              </div>
              <!-- 无优惠价格：有每日单价时突出单价，总价置于下方 -->
              <div v-else-if="!item.preferentialPrice">
                <template v-if="getUnitPriceValue(item)">
                  <div class="text-18px font-bold" :class="getIsImageBackground(item) ? 'subscribe-image-price' : 'subscribe-text-2'">
                    $ {{ getUnitPriceValue(item) }}<span class="text-12px font-normal" :class="getIsImageBackground(item) ? 'subscribe-text-4' : 'subscribe-text-2'"> / Day</span>
                  </div>
                  <div class="text-12px" :class="getIsImageBackground(item) ? 'subscribe-text-4' : 'subscribe-text-3'">
                    $ {{ item.price }}
                  </div>
                </template>
                <template v-else>
                  <div class="text-18px font-bold" :class="getIsImageBackground(item) ? 'subscribe-image-price' : 'subscribe-text-2'">
                    <span>$ {{ item.price }}</span>
                  </div>
                </template>
              </div>
              <!-- 有优惠价格 -->
              <div v-else>
                <div class="text-18px font-bold" :class="getIsImageBackground(item) ? 'subscribe-image-price' : 'subscribe-text-2'">
                  <div class="min-w-60px whitespace-nowrap">
                    <template v-if="shouldShowDailyPrice(item, isZero)">
                      $ {{ getDailyPrice(item) }}<span class="text-12px font-normal" :class="getIsImageBackground(item) ? 'subscribe-text-4' : 'subscribe-text-2'"> / Day</span>
                    </template>
                    <template v-else>
                      <span class="price-counter">
                        <span aria-hidden="true" class="invisible whitespace-nowrap">$ {{ Number(item.price).toFixed(2) }}</span>
                        <span class="price-counter__value">$ <countTo
                          :start-val="Number(item.price)"
                          :end-val="Number(item.preferentialPrice)"
                          :duration="3000"
                          :decimals="2"
                        /></span>
                      </span>
                    </template>
                  </div>
                </div>
                <div class="text-12px">
                  <span class="line-through" :class="getIsImageBackground(item) ? 'subscribe-text-4' : 'subscribe-text-3'">$ {{ item.price }}</span>
                  <span v-if="shouldShowPreferentialTotal(item, isZero)" class="font-medium ml-4px" :class="getIsImageBackground(item) ? 'subscribe-image-price' : 'subscribe-text-2'">$ {{ item.preferentialPrice }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="px-4px text-10px text-n-text-3 pb-100px">
        <p class="text-12px mt-6px">
          {{ config.texts.tips }}
        </p>
        <p class="mt-6px">
          {{ config.texts.tip1 }}
        </p>
        <p class="mt-6px">
          {{ config.texts.tip2 }}
        </p>
        <!-- 自动续费提示(仅自然用户展示;订阅中心无充值商品,不含括号内容) -->
        <AutoRenewalNotice class="mt-6px" has-subscribe :has-topup="false" prefix="3. " />
      </div>

      <!-- 支付方式选择器固定在底部 -->
      <div v-if="showPaymentMethodSelector" class="fixed bottom-0px left-0 right-0 flex justify-center">
        <PaymentMethodSelector
          variant="large"
          :dark="config.darkPaymentSelector"
          :model-value="currentPaymentMethod"
          :available-methods="availablePaymentMethods"
          @update:model-value="selectPaymentMethod"
        />
      </div>
    </div>

    <BasicModal v-model="discountModel" :transparent="true" class-name="discount-modal">
      <div @click="buyNow">
        <div class="w-[200px] flex justify-center absolute top-[40px] left-[50%] ml-[-100px]">
          <div class="text-white text-30px font-bold italic">
            {{ discountNumber }}<span class="text-20px">%</span>
            <div class="relative top-[-5px]">
              OFF
            </div>
          </div>
        </div>
        <img :src="config.resources.discountBg" width="300px">
      </div>
    </BasicModal>
  </div>
</template>

<style scoped>
.member-wrap {
  min-height: calc(100vh - 192px);
}

.subscribe-cover {
  width: 100%;
  background: v-bind(vipBgUrl) no-repeat top left;
  background-size: 100% auto;
}

.popular-tag {
  position: absolute;
  top: 0;
  left: 0;
  min-width: 56px;
  height: 18px;
  background: v-bind(redGradient);
  border-radius: 8px 0px 12px 0px;
  font-weight: 400;
  font-size: 10px;
  color: v-bind(whiteColor);
  text-align: center;
}
.cycleBg {
  background: v-bind(purpleTextGradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
}

.subscribe-text-1 {
  color: v-bind(subscribeText1);
}
.subscribe-text-2 {
  color: v-bind(subscribeText2);
}
.subscribe-text-3 {
  color: v-bind(subscribeText3);
}
/* 图片背景卡上的次级文案（描述/划线价/Day 单位）：浅色系 text-1/2/3 在金色图卡上不可见，
   此前模板已引用该类但未定义样式，靠容器内联色继承生效，这里补上显式定义 */
.subscribe-text-4 {
  color: v-bind(subscribeText4);
}
/* 图片背景卡上的金额：未配置 subscribeImagePrice 的品牌回退 subscribeText2，与原样式一致 */
.subscribe-image-price {
  color: v-bind(subscribeImagePrice);
}

/* 价格倒数动画：用不可见的原价占位撑出固定宽度，动画数字绝对定位叠加其上，
   避免 countTo 逐帧改变数字宽度导致同行内容重排、卡片高度上下跳动 */
.price-counter {
  position: relative;
  display: inline-block;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.price-counter__value {
  position: absolute;
  top: 0;
  right: 0;
  white-space: nowrap;
}
</style>

<style lang="scss">
.discount-modal {
  .close-icon {
    top: 12px;
    right: 0;
  }
}
</style>
